// FileField (Components, BL-189): a labelled box that takes a file from this device (Banking's statement,
// a CSV or OFX from the bank's site). Drawn as Field draws a box (DESIGN.md §8's field, 38 tall): its label
// above, muted; in the box "Choose a file" in ink, then the chosen file's name, or "No file chosen"
// muted; what it's for under it; a problem with the file under that, in ink words after StateMark's red
// bar, with a red line round the box (§2). The browser's own file input lies over the box, unseen, so
// a click, the keyboard and a screen reader all reach the real control. It reads the file no further:
// onChoose hands it to the caller. Reads only --filefield-*.
import { useId, useState, type ReactNode } from 'react';
import { StateMark } from '../StateMark/StateMark.tsx';
import './FileField.css';

export interface FileFieldProps {
  label: string;
  /** The kinds it takes, as the browser's accept says them (".csv,.ofx,text/csv"). */
  accept?: string;
  /** What it's for, under it ("A CSV or OFX file from your bank’s website."). */
  help?: ReactNode;
  /** What's wrong with the chosen file, in plain words; under it, after a red bar. */
  error?: string | null;
  isDisabled?: boolean;
  /** The file chosen; its name then shows in the box. */
  onChoose: (file: File) => void;
}

export function FileField({ label, accept, help, error, isDisabled, onChoose }: FileFieldProps) {
  const id = useId();
  const [name, setName] = useState<string | null>(null);
  const helpId = help ? `${id}-help` : undefined;
  const errId = error ? `${id}-error` : undefined;
  return (
    <div className="fl-filefield">
      <label className="fl-filefield__label" htmlFor={id}>
        {label}
      </label>
      <span
        className="fl-filefield__box"
        data-invalid={error ? '' : undefined}
        data-disabled={isDisabled ? '' : undefined}
      >
        <input
          id={id}
          className="fl-filefield__input"
          type="file"
          {...(accept ? { accept } : {})}
          disabled={isDisabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={[helpId, errId].filter(Boolean).join(' ') || undefined}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            setName(f.name);
            onChoose(f);
          }}
        />
        <span className="fl-filefield__choose" aria-hidden="true">
          Choose a file
        </span>
        <span className="fl-filefield__name" data-none={name ? undefined : ''} aria-hidden="true">
          {name ?? 'No file chosen'}
        </span>
      </span>
      {help ? (
        <span id={helpId} className="fl-filefield__help">
          {help}
        </span>
      ) : null}
      {error ? (
        <span id={errId} className="fl-filefield__error">
          <StateMark state="problem" />
          {error}
        </span>
      ) : null}
    </div>
  );
}
